import React, { useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { updateEmployee } from "../../api/employees";
import { getBranchLabel } from "../../utils/dataMappers";
import { employeeRequiresShiftHandover, isModeratorJobTitle } from "../../utils/orgHelpers";
import "./HrWorkModeManagement.css";

const WORK_MODE_OPTIONS = [
  {
    value: "OFFICE",
    label: "بصمة (مكتب)",
    hint: "الحضور عبر جهاز البصمة فقط",
  },
  {
    value: "REMOTE",
    label: "من التطبيق (عن بُعد)",
    hint: "حضور وانصراف من الأبلكيشن يوميًا",
  },
  {
    value: "HYBRID",
    label: "مختلط",
    hint: "مكتب + يمكن طلب أيام عمل من المنزل",
  },
];

const WORK_MODE_BADGE = {
  OFFICE: "wm-badge-office",
  REMOTE: "wm-badge-remote",
  HYBRID: "wm-badge-hybrid",
};

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

export default function HrWorkModeManagement() {
  const { employeesByBranch, branches, refreshData } = useData();
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("ALL");
  const [filterFlexible, setFilterFlexible] = useState("ALL");
  const [filterHandover, setFilterHandover] = useState("ALL");
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const branchMap = useMemo(() => {
    const map = {};
    (branches || []).forEach((b) => {
      map[b.id] = b.name || b.code || b.id;
    });
    return map;
  }, [branches]);

  const employees = useMemo(() => {
    return Object.values(employeesByBranch || {})
      .flat()
      .filter((e) => e && e.id && e.status !== "terminated")
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "ar"),
      );
  }, [employeesByBranch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((emp) => {
      const mode = emp.workMode || "OFFICE";
      if (filterMode !== "ALL" && mode !== filterMode) return false;
      if (filterFlexible === "FLEXIBLE" && !emp.manualAttendanceOnly) return false;
      if (filterFlexible === "NORMAL" && emp.manualAttendanceOnly) return false;
      if (filterHandover === "HANDOVER" && !employeeRequiresShiftHandover(emp)) {
        return false;
      }
      if (!q) return true;
      return (
        String(emp.name || "").toLowerCase().includes(q) ||
        String(emp.code || "").toLowerCase().includes(q) ||
        String(emp.jobTitle || "").toLowerCase().includes(q)
      );
    });
  }, [employees, search, filterMode, filterFlexible, filterHandover]);

  const counts = useMemo(() => {
    const base = {
      ALL: employees.length,
      OFFICE: 0,
      REMOTE: 0,
      HYBRID: 0,
      FLEXIBLE: 0,
      HANDOVER: 0,
    };
    employees.forEach((emp) => {
      const mode = emp.workMode || "OFFICE";
      if (base[mode] != null) base[mode] += 1;
      if (emp.manualAttendanceOnly) base.FLEXIBLE += 1;
      if (employeeRequiresShiftHandover(emp)) base.HANDOVER += 1;
    });
    return base;
  }, [employees]);

  async function handleChangeMode(emp, nextMode) {
    const current = emp.workMode || "OFFICE";
    if (nextMode === current) return;

    setError("");
    setSuccess("");
    setSavingId(emp.id);
    try {
      await updateEmployee(emp.id, { workMode: nextMode });
      await refreshData?.();
      const label =
        WORK_MODE_OPTIONS.find((o) => o.value === nextMode)?.label || nextMode;
      setSuccess(`تم تحديث نظام حضور «${emp.name}» إلى: ${label}`);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث نظام الحضور"));
    } finally {
      setSavingId(null);
    }
  }

  async function handleToggleFlexible(emp, nextValue) {
    if (Boolean(emp.manualAttendanceOnly) === nextValue) return;

    setError("");
    setSuccess("");
    setSavingId(emp.id);
    try {
      await updateEmployee(emp.id, { manualAttendanceOnly: nextValue });
      await refreshData?.();
      setSuccess(
        nextValue
          ? `تم تفعيل الساعات المرنة لـ «${emp.name}» (حضور افتراضي — HR يسجّل الغياب/الإجازة)`
          : `تم إلغاء الساعات المرنة لـ «${emp.name}»`,
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث الساعات المرنة"));
    } finally {
      setSavingId(null);
    }
  }

  async function handleToggleHandover(emp, nextValue) {
    if (Boolean(emp.requiresShiftHandover) === nextValue) return;

    setError("");
    setSuccess("");
    setSavingId(emp.id);
    try {
      await updateEmployee(emp.id, { requiresShiftHandover: nextValue });
      await refreshData?.();
      setSuccess(
        nextValue
          ? `تم تفعيل استلام الشيفت لـ «${emp.name}» — الانصراف بعد حضور الموديريتور التالية فقط`
          : `تم إلغاء استلام الشيفت لـ «${emp.name}»`,
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث استلام الشيفت"));
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="wm-page" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">نظام حضور الموظفين</h1>
        <p className="ess-home-subtitle">
          حدّد لكل موظف: بصمة، حضور من التطبيق، أو نظام مختلط — ويمكن تفعيل
          ساعات مرنة واستلام الشيفت بين الموديريتور
        </p>
      </div>

      <div className="wm-legend">
        {WORK_MODE_OPTIONS.map((opt) => (
          <div key={opt.value} className="wm-legend-item">
            <span className={`wm-badge ${WORK_MODE_BADGE[opt.value]}`}>
              {opt.label}
            </span>
            <span>{opt.hint}</span>
          </div>
        ))}
        <div className="wm-legend-item">
          <span className="wm-badge wm-badge-flexible">ساعات مرنة</span>
          <span>حضور افتراضي بدون بصمة — الـHR يسجّل الغياب أو الإجازة فقط</span>
        </div>
        <div className="wm-legend-item">
          <span className="wm-badge wm-badge-handover">استلام شيفت</span>
          <span>الموديريتور لا تسجّل انصراف إلا بعد حضور الموديريتور التالية</span>
        </div>
      </div>

      <div className="wm-toolbar">
        <input
          type="search"
          className="wm-search"
          placeholder="بحث بالاسم أو الكود أو المسمى..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="wm-filters">
          {[
            { value: "ALL", label: `الكل (${counts.ALL})` },
            { value: "OFFICE", label: `بصمة (${counts.OFFICE})` },
            { value: "REMOTE", label: `تطبيق (${counts.REMOTE})` },
            { value: "HYBRID", label: `مختلط (${counts.HYBRID})` },
          ].map((f) => (
            <button
              key={f.value}
              type="button"
              className={`wm-filter-btn${filterMode === f.value ? " active" : ""}`}
              onClick={() => setFilterMode(f.value)}
            >
              {f.label}
            </button>
          ))}
          <button
            type="button"
            className={`wm-filter-btn${filterFlexible === "FLEXIBLE" ? " active" : ""}`}
            onClick={() =>
              setFilterFlexible((prev) =>
                prev === "FLEXIBLE" ? "ALL" : "FLEXIBLE",
              )
            }
          >
            ساعات مرنة ({counts.FLEXIBLE})
          </button>
          <button
            type="button"
            className={`wm-filter-btn${filterHandover === "HANDOVER" ? " active" : ""}`}
            onClick={() =>
              setFilterHandover((prev) =>
                prev === "HANDOVER" ? "ALL" : "HANDOVER",
              )
            }
          >
            استلام شيفت ({counts.HANDOVER})
          </button>
        </div>
      </div>

      {error && <div className="wm-alert wm-alert-error">{error}</div>}
      {success && <div className="wm-alert wm-alert-success">{success}</div>}

      <div className="wm-table-wrap">
        <table className="wm-table">
          <thead>
            <tr>
              <th>الموظف</th>
              <th>المسمى</th>
              <th>الفرع</th>
              <th>النظام الحالي</th>
              <th>تغيير النظام</th>
              <th>ساعات مرنة</th>
              <th>استلام شيفت</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="wm-empty">
                  لا يوجد موظفون مطابقون للبحث
                </td>
              </tr>
            ) : (
              filtered.map((emp) => {
                const mode = emp.workMode || "OFFICE";
                const option = WORK_MODE_OPTIONS.find((o) => o.value === mode);
                const flexible = Boolean(emp.manualAttendanceOnly);
                const handoverOn = employeeRequiresShiftHandover(emp);
                return (
                  <tr key={emp.id}>
                    <td data-label="الموظف">
                      <div className="wm-emp-name">{emp.name}</div>
                      <div className="wm-emp-code">{emp.code}</div>
                    </td>
                    <td data-label="المسمى">{emp.jobTitle || "—"}</td>
                    <td data-label="الفرع">
                      {getBranchLabel(emp.branchId, branchMap)}
                    </td>
                    <td data-label="النظام الحالي">
                      <span className={`wm-badge ${WORK_MODE_BADGE[mode]}`}>
                        {option?.label || mode}
                      </span>
                      {flexible && (
                        <span className="wm-badge wm-badge-flexible">مرن</span>
                      )}
                      {handoverOn && (
                        <span className="wm-badge wm-badge-handover">شيفت</span>
                      )}
                    </td>
                    <td data-label="تغيير النظام">
                      <select
                        className="wm-select"
                        value={mode}
                        disabled={savingId === emp.id}
                        onChange={(e) => handleChangeMode(emp, e.target.value)}
                      >
                        {WORK_MODE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {savingId === emp.id && (
                        <span className="wm-saving">جاري الحفظ...</span>
                      )}
                    </td>
                    <td data-label="ساعات مرنة">
                      <label className="wm-flexible-toggle">
                        <input
                          type="checkbox"
                          checked={flexible}
                          disabled={savingId === emp.id}
                          onChange={(e) =>
                            handleToggleFlexible(emp, e.target.checked)
                          }
                        />
                        <span>يدوي فقط</span>
                      </label>
                    </td>
                    <td data-label="استلام شيفت">
                      <label className="wm-flexible-toggle">
                        <input
                          type="checkbox"
                          checked={handoverOn}
                          disabled={
                            savingId === emp.id || isModeratorJobTitle(emp.jobTitle)
                          }
                          onChange={(e) =>
                            handleToggleHandover(emp, e.target.checked)
                          }
                        />
                        <span>تسليم شيفت</span>
                      </label>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
